import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import type {
  ConfigurableProperty,
  DigitalTwinSettingTarget,
  DigitalTwinTranslations,
} from 'api/digitalTwin/digitalTwin.api'
import { useUiConfig } from 'api/uiConfig/uiConfig.api'
import { toOptJobRuns } from 'helpers/appliedSettingValues.helper/optJobRuns.helper'
import { buildSeriesCatalogue } from 'helpers/appliedSettingValues.helper/seriesCatalogue.helper'
import { getAttributeChips } from 'helpers/appliedSettingValues.helper/seriesSelection.helper'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { removeFilterFromUrl } from 'store/filter/urlFilter.helper'
import { Button, LoadingPlaceholderContainer } from 'ui/atoms'
import NewCheckbox from 'ui/atoms/NewCheckbox/NewCheckbox'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import SearchField from 'ui/atoms/SearchField/SearchField'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { useSystemFeatureFlagIsActive } from 'utils/systemUtils'
import {
  FILTER_BY_OUTCOME_SYSTEM_FEATURE_FLAG,
  getAppliedSettingValuesStatus,
  MAX_APPLIED_SETTING_COLUMNS,
} from './AppliedSettingValues.helper'
import { AppliedSettingValuesContent } from './AppliedSettingValuesContent'
import { AppliedSettingValuesEmptyState } from './AppliedSettingValuesEmptyState'
import { useRememberedPropertyChips } from './useRememberedPropertyChips'

const BASE_CONFIG = 'base_config'

type AppliedSettingValuesProps = {
  period: { startTime: ISODateTime; endTime: ISODateTime }
  optimalSubtype?: string
  overrideAlias: UiConfigAliases
  /** Highest priority level first. */
  priorityLevels: number[]
  allProperties: ConfigurableProperty[]
  /** The filter categories and options the header offers; a saved filter is read against them. */
  availableFiltersSubset: string[][]
  /** The priority levels of the chosen optimization type, which decide which settings a filter selects. */
  selectedPriorityLevels: number[]
  selectedProperties: DigitalTwinSettingTarget[]
  isFiltered: boolean
  canNarrowSelection?: boolean
  translations: DigitalTwinTranslations
}

/** The applied values of the settings the header filter selects, with their units' outcome and optimal. */
export function AppliedSettingValues({
  period,
  optimalSubtype,
  overrideAlias,
  priorityLevels,
  allProperties,
  availableFiltersSubset,
  selectedPriorityLevels,
  selectedProperties,
  isFiltered,
  canNarrowSelection = true,
  translations,
}: AppliedSettingValuesProps): ReactNode {
  const { t } = useTranslation()
  const [showGraph, setShowGraph] = useState(true)
  const [hideEmptyColumns, setHideEmptyColumns] = useState(true)
  const [onlyUnitsWithOutcome, setOnlyUnitsWithOutcome] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const {
    attributes: rememberedAttributes,
    setAttributes: setSelectedAttributes,
    isExpanded: showPropertyChips,
    setIsExpanded: setShowPropertyChips,
  } = useRememberedPropertyChips()
  const navigate = useNavigate()
  const { activeSystem } = useAuth()
  const hasFilterByOutcome = useSystemFeatureFlagIsActive(FILTER_BY_OUTCOME_SYSTEM_FEATURE_FLAG)
  const { data: uiConfigs, status: baseConfigState } = useUiConfig(activeSystem?.id, BASE_CONFIG)
  const baseConfig = uiConfigs?.find(({ component }) => component === BASE_CONFIG)
  const catalogue = baseConfig ? buildSeriesCatalogue(baseConfig, allProperties, translations, overrideAlias) : []
  const runs = toOptJobRuns({ optimalSubtype, overrideAlias, systemSubtypes: activeSystem?.subtypes })
  const status = getAppliedSettingValuesStatus({
    baseConfig,
    baseConfigState,
    isFiltered,
    catalogue,
    selectedProperties,
  })

  const optimizationTypeKey = JSON.stringify([overrideAlias, optimalSubtype])

  switch (status.kind) {
    case 'loadingModel':
      return <LoadingPlaceholderContainer height={100} />
    case 'unavailable':
      return <p>{t('Applied setting values could not be loaded')}</p>
    case 'noSelection':
      return (
        <AppliedSettingValuesEmptyState
          availableFiltersSubset={availableFiltersSubset}
          allProperties={allProperties}
          priorityLevels={selectedPriorityLevels}
          catalogue={catalogue}
          translations={translations}
        />
      )
    case 'noMatch':
      return (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <NewIcon icon="fal fa-filter" color="grey" size="large" ariaLabel="" />
          <h3 className="m-0 text-xl font-bold text-grey-8">
            {canNarrowSelection ? t('No series match the filter') : t('No applied setting values')}
          </h3>
          <p className="m-0 mb-2 max-w-xl text-grey-6">
            {canNarrowSelection
              ? t(
                  'None of the selected units and properties have applied setting values in this period. Try a different selection, or reset the filter to start over.'
                )
              : t('None of the units and properties on this page have applied setting values in this period.')}
          </p>
          {canNarrowSelection && (
            <Button primary onClick={() => removeFilterFromUrl(navigate)}>
              {t('Reset filters')}
            </Button>
          )}
        </div>
      )
    case 'tooManySeries':
      return (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <NewIcon icon="fal fa-filter" color="grey" size="large" ariaLabel="" />
          <h3 className="m-0 text-xl font-bold text-grey-8">{t('Too many columns selected')}</h3>
          <p className="m-0 max-w-xl text-grey-6">
            {canNarrowSelection
              ? t('The filter selects {{columnCount}} columns. Narrow the filter to at most {{max}} columns.', {
                  columnCount: status.columnCount,
                  max: MAX_APPLIED_SETTING_COLUMNS,
                })
              : t('The page has {{columnCount}} columns, more than the {{max}} that can be shown.', {
                  columnCount: status.columnCount,
                  max: MAX_APPLIED_SETTING_COLUMNS,
                })}
          </p>
        </div>
      )
    case 'selected': {
      const attributeChips = getAttributeChips(status.selectedSeries, translations)
      // With a single property there is no chip row to collapse or narrow by. Chips remembered from another
      // selection that this one lacks narrow nothing.
      const hasPropertyChips = attributeChips.length > 1
      const selectedAttributes = hasPropertyChips
        ? rememberedAttributes.filter((attribute) => attributeChips.some((chip) => chip.attribute === attribute))
        : []
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-x-3">
            <div>
              <SearchField
                value={searchTerm}
                onChange={(newSearchTerm) => setSearchTerm(String(newSearchTerm))}
                onClear={() => setSearchTerm('')}
              />
            </div>
            <NewCheckbox
              label={t('Show graph')}
              checked={showGraph}
              onChange={(event) => setShowGraph(event.target.checked)}
            />
            <NewCheckbox
              label={t('Hide empty columns')}
              checked={hideEmptyColumns}
              onChange={(event) => setHideEmptyColumns(event.target.checked)}
            />
            {hasFilterByOutcome && (
              <Tooltip
                arrow
                describeChild
                title={t('Show only units with an outcome in the loaded period. Zooming changes the period.')}
              >
                <span>
                  <NewCheckbox
                    label={t('Filter by outcome')}
                    checked={onlyUnitsWithOutcome}
                    onChange={(event) => setOnlyUnitsWithOutcome(event.target.checked)}
                  />
                </span>
              </Tooltip>
            )}
            {hasPropertyChips && (
              <Chip
                label={
                  <span className="inline-flex items-center gap-1.5">
                    {selectedAttributes.length > 0
                      ? t('Properties ({{count}} selected)', { count: selectedAttributes.length })
                      : t('Properties')}
                    <i
                      aria-hidden
                      className={showPropertyChips ? 'fal fa-chevron-down text-xs' : 'fal fa-chevron-right text-xs'}
                    />
                  </span>
                }
                clickable
                aria-expanded={showPropertyChips}
                color={selectedAttributes.length > 0 ? 'primary' : 'default'}
                onClick={() => setShowPropertyChips(!showPropertyChips)}
              />
            )}
          </div>
          <AppliedSettingValuesContent
            key={optimizationTypeKey}
            selectedSeries={status.selectedSeries}
            runs={runs}
            overrideAlias={overrideAlias}
            period={period}
            priorityLevels={priorityLevels}
            translations={translations}
            showGraph={showGraph}
            searchTerm={searchTerm}
            onlyUnitsWithOutcome={onlyUnitsWithOutcome}
            showPropertyChips={showPropertyChips}
            attributeChips={attributeChips}
            selectedAttributes={selectedAttributes}
            onSelectedAttributesChange={setSelectedAttributes}
            hideEmptyColumns={hideEmptyColumns}
          />
        </div>
      )
    }
  }
}
